import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Cog, Users, TrendingUp, Rocket, Quote, MapPin } from "lucide-react";

const AMDEN_ONE_POINTS = [
  ["Practical over flashy", "Every screen follows a clear, consistent frame so you always know what to do next — no clutter, no jargon."],
  ["Secure by design", "Strict tenant isolation, role-based access, audit trails and encrypted transport are built in from day one."],
  ["Built to grow with you", "Start free as a sole trader and scale up to a full team without ever switching tools."],
];

const PILLARS = [
  [Cog, "Operations", "Systems, processes & efficiency"],
  [Users, "People", "HR, recruitment & team building"],
  [TrendingUp, "Growth", "Marketing, strategy & revenue"],
  [Rocket, "Launch", "New business support"],
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* SECTION 1 — Amden One (the product) */}
      <section className="bg-navy grain-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 text-center">
          <span className="text-xs font-semibold tracking-[0.2em] text-blue-accent uppercase">About the platform</span>
          <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight">About Amden One</h1>
          <p className="mt-5 text-lg text-slate-300 max-w-2xl mx-auto">
            Amden One is the all-in-one operations platform for trades, service firms and small teams — CRM, quotes,
            invoices, jobs, planner and Alfie AI, in one simple system.
          </p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-10">
        <img src="/mockups/dashboard.png" alt="The Amden One dashboard" loading="lazy"
          className="rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full object-cover animate-fade-up" />
      </div>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid gap-6 sm:grid-cols-3">
          {AMDEN_ONE_POINTS.map(([t, d], i) => (
            <div key={t} className="amden-card p-6 animate-fade-up" style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="h-8 w-8 rounded-lg bg-blue-600 text-white grid place-items-center font-mono font-semibold text-sm">{i + 1}</div>
              <h3 className="mt-4 font-semibold text-navy text-lg">{t}</h3>
              <p className="mt-2 text-slate-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SECTION 2 — Amden Group (the company behind it) */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] text-blue-accent uppercase">The company behind Amden One</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight text-navy">
                About Amden <span className="text-blue-accent">Group</span>
              </h2>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">
                Amden Group is a business support partner helping organisations improve operations, build strong teams
                and drive growth. We combine consultancy with execution across Operations, People, Growth and Business Launch.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                {PILLARS.map(([Icon, name, desc]) => (
                  <div key={name} className="flex items-start gap-3 rounded-xl bg-white border border-slate-200 p-4" data-testid={`pillar-${name.toLowerCase()}`}>
                    <span className="h-10 w-10 rounded-lg bg-blue-50 text-blue-accent grid place-items-center shrink-0"><Icon size={20} /></span>
                    <div>
                      <p className="font-semibold text-navy">{name}</p>
                      <p className="text-sm text-slate-500">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="https://amden.ie/assets/about-office.jpg" alt="The Amden Group team at work in their office"
                loading="lazy" className="rounded-2xl shadow-xl ring-1 ring-slate-200 w-full h-80 object-cover" />
              <div className="absolute -bottom-5 left-5 right-5 sm:left-8 sm:right-auto rounded-xl bg-white shadow-lg border border-slate-100 px-4 py-3 flex items-center gap-2">
                <Quote size={16} className="text-blue-accent shrink-0" />
                <p className="text-sm font-medium text-navy">Advice that guides. Solutions that deliver.</p>
              </div>
            </div>
          </div>

          {/* Our story */}
          <div className="mt-24 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="text-xs font-semibold tracking-[0.2em] text-slate-400 uppercase">01 — Our story</span>
              <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-navy leading-snug">
                From SME support to a scalable business partner
              </h3>
              <div className="mt-5 space-y-4 text-slate-600 leading-relaxed">
                <p>Originally focused on supporting SMEs, Amden has evolved into a scalable business support model that now works with both growing businesses and larger organisations.</p>
                <p>Our focus remains the same — delivering practical, hands-on support that makes a real difference. We understand that every business is different, which is why we tailor our approach to your specific needs and challenges.</p>
                <p>Today, we're proud to partner with businesses across Ireland and beyond, helping them build better, stronger, more scalable operations.</p>
              </div>
              <blockquote className="mt-6 border-l-4 border-blue-accent pl-4 italic text-navy">
                Practical, hands-on support that makes a real difference.
              </blockquote>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="The Amden team working through a business plan with clients" loading="lazy"
                className="rounded-2xl shadow-xl ring-1 ring-slate-200 w-full h-80 object-cover" />
              <div className="absolute -bottom-4 right-5 rounded-xl bg-white shadow-lg border border-slate-100 px-4 py-2.5 flex items-center gap-2">
                <MapPin size={16} className="text-blue-accent shrink-0" />
                <p className="text-sm font-medium text-navy">Kells, Co. Meath, Ireland</p>
              </div>
            </div>
          </div>

          <div className="mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://www.amden.ie" target="_blank" rel="noreferrer" data-testid="amden-website-link"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-navy hover:bg-slate-800 text-white font-semibold shadow-lg transition-colors">
              Visit www.amden.ie <ExternalLink size={17} />
            </a>
            <Link to="/register" data-testid="about-start-free"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg transition-colors">
              Start free with Amden One <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
